import { ExternalLink, Search } from "@procore/core-icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ListNode } from "components/objectTree/types";
import { makeNodeList } from "components/objectTree/utils";
import { SlottedWindow } from "components/window/SlottedWindow";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { type BaseWindowProps } from "contexts/windows";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { ModelTree } from "features/modelTree/components/modelTree";
import { emptyFileTypeFilters } from "features/modelTree/constants";
import { ModelTreeNodeData } from "features/modelTree/types";
import {
    renderActions,
    selectDefaultVisibility,
    selectMainObject,
    selectPreIsolationVisibility,
} from "features/render";
import { ObjectVisibility } from "features/render/types";
import { useAbortController } from "hooks/useAbortController";
import { useObjectTreeSearch } from "hooks/useObjectTreeSearch";
import { telemetry } from "telemetry";
import {
    bulkSelectAllHierarchies,
    hideObjectHierarchy,
    isolateObjects,
    isolateObjectsInXRayMode,
    selectObjectHierarchy,
    showObjectHierarchy,
    unisolateObjects,
    unisolateObjectsFromXRayMode,
    unselectObjectHierarchy,
} from "utils/objectActions";

import { ContextMenuPopup } from "../hooks/ContextMenuPopup";
import { ObjectTreeNodeProcoreV2 } from "./components/ObjectTreeNodeProcoreV2";
import { SelectionContext } from "./selectionContext";
import { BulkAction } from "./utils/bulkActions";

const ITEM_HEIGHT_V2 = 30;

// Viewport picks where highlightedIds.size exceeds this are treated as stale (from a
// prior tree-parent selection that highlighted an entire subtree). Fresh viewport
// single-clicks reset highlighted to 1; Ctrl+click grows it by 1 per pick.
const VIEWER_MULTISELECT_THRESHOLD = 50;

const enum SelectionMode {
    Range = "range",
    Toggle = "toggle",
    Single = "single",
}

function getSelectionMode(event: React.MouseEvent, hasAnchor: boolean): SelectionMode {
    if (event.shiftKey && hasAnchor) {
        return SelectionMode.Range;
    }
    if (event.ctrlKey || event.metaKey) {
        return SelectionMode.Toggle;
    }
    return SelectionMode.Single;
}

type ListItem = ReturnType<typeof makeNodeList>[number];

function computeNewSelectedPaths(
    mode: SelectionMode,
    path: string,
    list: ListItem[],
    selectedRowPaths: Set<string>,
    anchorRowPath: string | null,
): { paths: Set<string>; nextAnchor: string | null } {
    switch (mode) {
        case SelectionMode.Range: {
            const anchorIdx = list.findIndex((i) => i.node.path === anchorRowPath);
            const clickedIdx = list.findIndex((i) => i.node.path === path);
            if (anchorIdx !== -1 && clickedIdx !== -1) {
                const [from, to] = anchorIdx < clickedIdx ? [anchorIdx, clickedIdx] : [clickedIdx, anchorIdx];
                return { paths: new Set(list.slice(from, to + 1).map((i) => i.node.path)), nextAnchor: anchorRowPath };
            }
            return { paths: new Set([path]), nextAnchor: anchorRowPath };
        }
        case SelectionMode.Toggle: {
            // Intentional toggle: Cmd/Ctrl+clicking an already-selected row deselects it.
            // BM-12619 inadvertently reverts this to always-add — keep this behavior on rebase.
            const paths = new Set(selectedRowPaths);
            if (paths.has(path)) {
                paths.delete(path);
                return { paths, nextAnchor: anchorRowPath };
            }
            paths.add(path);
            return { paths, nextAnchor: path };
        }
        case SelectionMode.Single: {
            const isOnlySelected = selectedRowPaths.size === 1 && selectedRowPaths.has(path);
            return {
                paths: isOnlySelected ? new Set() : new Set([path]),
                nextAnchor: isOnlySelected ? null : path,
            };
        }
    }
}

export function ObjectTreeWindowV2({ onClose, position }: BaseWindowProps) {
    const { t } = useTranslation();
    const {
        state: { db },
    } = useExplorerGlobals(true);

    const { searchTerm, filteredObjectIds, tree, setTree, treeKey, isSearching, isFilterActive, handleSearchChange } =
        useObjectTreeSearch(db);

    const dispatch = useAppDispatch();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const hidden = useHidden();
    const mainObject = useAppSelector(selectMainObject);
    const { ids: highlightedIds } = useHighlighted();
    const [abortController] = useAbortController();
    const [loading, setLoading] = useState(false);
    const { flyToSelected } = useFlyToSelected();

    const [selectedRowPaths, setSelectedRowPaths] = useState<Set<string>>(new Set());
    const anchorRowPathRef = useRef<string | null>(null);
    // Set to true when the selection was initiated from within the tree so the
    // mainObject effect doesn't clear selectedRowPaths on the resulting Redux update.
    const treeInitiatedRef = useRef(false);
    // ID of a viewer-originated pick waiting for ModelTree to load its node into the tree.
    const pendingViewerSelectRef = useRef<number | null>(null);
    // Incremented on each row selection; lets concurrent async invocations detect they've been superseded.
    const selectGenerationRef = useRef(0);
    // Read-only ref so effects can read the current tree without listing it as a dep.
    const treeRef = useRef(tree);
    treeRef.current = tree;
    // Read-only ref so the mainObject effect can read the latest highlighted IDs
    // without listing them as a dependency (avoids double-firing on viewer picks).
    const highlightedIdsRef = useRef(highlightedIds);
    highlightedIdsRef.current = highlightedIds;
    // True while the active selection originated from a viewer pick (not a tree row click).
    // Lets the highlightedIds effect re-sync tree rows when highlighted updates in a
    // separate render from mainObject (React 18 doesn't always batch cross-store dispatches).
    const isViewerPickRef = useRef(false);
    const viewerPickMainObjectRef = useRef<number | null>(null);

    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);

    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

    const list = useMemo(() => makeNodeList({ tree }), [tree]);

    // True when every selected row is hidden — drives the Hide/Unhide menu label.
    const allSelectedHidden =
        selectedRowPaths.size > 0 && [...selectedRowPaths].every((p) => hidden.ids.has(tree[p]?.data.object.id));

    // True when the scene is in plain (transparent) isolate mode.
    const isIsolated =
        selectedRowPaths.size > 0 &&
        !allSelectedHidden &&
        defaultVisibility === ObjectVisibility.Transparent &&
        preIsolationVisibility !== undefined;

    // True when the scene is already in X-Ray isolate mode triggered by a prior isolate action.
    const isIsolatedInXRay =
        selectedRowPaths.size > 0 &&
        !allSelectedHidden &&
        defaultVisibility === ObjectVisibility.Xray &&
        preIsolationVisibility !== undefined;

    // Viewer-originated pick: sync highlighted IDs to tree row selection.
    // When highlighted is small (fresh viewport picks), expands to all matching rows.
    // When highlighted is large (stale from a prior tree-parent selection), falls back
    // to selecting only the primary node to avoid spurious multi-row expansion.
    useEffect(() => {
        if (treeInitiatedRef.current) {
            treeInitiatedRef.current = false;
            isViewerPickRef.current = false;
            return;
        }

        const currentHighlighted = highlightedIdsRef.current;
        // Viewer resets highlighted to 1 on a fresh object pick (setIds); it only appends
        // (addIds) on Ctrl+click multi-select. If the set is large, it's almost certainly
        // stale from a prior tree-parent selection that highlighted an entire subtree.
        const isFreshViewerSelection = currentHighlighted.size <= VIEWER_MULTISELECT_THRESHOLD;

        anchorRowPathRef.current = null;
        pendingViewerSelectRef.current = null;

        if (mainObject == null) {
            const prevHighlighted = highlightedIdsRef.current;
            if (
                isViewerPickRef.current &&
                prevHighlighted.size > 0 &&
                prevHighlighted.size <= VIEWER_MULTISELECT_THRESHOLD
            ) {
                // Viewer deselected the primary object (Ctrl+click toggle-off) but the
                // snapshot still has remaining highlighted objects. The viewer will
                // subsequently clear highlighted entirely, so we capture the remaining
                // set NOW and stop listening (isViewerPickRef=false) so the re-sync
                // effect doesn't wipe this out when highlighted hits 0.
                const remainingPaths = new Set<string>();
                for (const node of Object.values(treeRef.current)) {
                    if (node.path !== "" && prevHighlighted.has(node.data.object.id)) {
                        remainingPaths.add(node.path);
                    }
                }
                setSelectedRowPaths(remainingPaths);
                // Keep isViewerPickRef=true so the re-sync effect can continue tracking
                // highlighted changes (the viewer often removes one extra object after a
                // Ctrl+click deselect, and subsequent deselects need the same path).
                viewerPickMainObjectRef.current = null;
                return;
            }
            isViewerPickRef.current = false;
            viewerPickMainObjectRef.current = null;
            setSelectedRowPaths(new Set());
            return;
        }

        isViewerPickRef.current = true;
        viewerPickMainObjectRef.current = mainObject;

        let mainNodePath: string | null = null;
        const matchingPaths = new Set<string>();

        for (const node of Object.values(treeRef.current)) {
            if (node.path === "") {
                continue;
            }
            if (isFreshViewerSelection && currentHighlighted.has(node.data.object.id)) {
                matchingPaths.add(node.path);
            }
            if (node.data.object.id === mainObject) {
                mainNodePath = node.path;
                if (!isFreshViewerSelection) {
                    break;
                }
            }
        }

        if (mainNodePath) {
            if (isFreshViewerSelection) {
                if (!matchingPaths.has(mainNodePath)) {
                    matchingPaths.clear();
                }
                matchingPaths.add(mainNodePath);
                setSelectedRowPaths(matchingPaths);
            } else {
                setSelectedRowPaths(new Set([mainNodePath]));
            }
            anchorRowPathRef.current = mainNodePath;
        } else {
            setSelectedRowPaths(new Set());
            pendingViewerSelectRef.current = mainObject;
        }
    }, [mainObject]);

    // Fallback: once ModelTree expands the ancestor chain and loads the pending node,
    // scan highlighted IDs again to pick up the full multi-select set.
    useEffect(() => {
        if (pendingViewerSelectRef.current == null) {
            return;
        }
        const targetId = pendingViewerSelectRef.current;
        const targetNode = Object.values(tree).find((n) => n.data.object.id === targetId);
        if (targetNode) {
            pendingViewerSelectRef.current = null;
            // Select only the target node. The highlighted set at this point includes
            // accumulated IDs from previous clicks (viewer dispatches addIds, not setIds),
            // so using matchingPaths here would cause spurious multi-row selection.
            setSelectedRowPaths(new Set([targetNode.path]));
            anchorRowPathRef.current = targetNode.path;
        }
    }, [tree]);

    // Re-sync tree row selection when highlighted changes from a viewer pick.
    // Handles the React 18 unbatched case where the viewer's addIds (Ctrl+click) fires
    // in a later render than mainObject, so the mainObject effect snapshot only sees N-1 IDs.
    // Guard: only re-sync when set is small (fresh viewer multi-select); skip when large (stale
    // from a prior tree-parent selectObjectHierarchy that highlighted an entire subtree).
    useEffect(() => {
        if (!isViewerPickRef.current) {
            return;
        }
        if (highlightedIds.size === 0) {
            isViewerPickRef.current = false;
            viewerPickMainObjectRef.current = null;
            setSelectedRowPaths(new Set());
            return;
        }
        if (highlightedIds.size > VIEWER_MULTISELECT_THRESHOLD) {
            return;
        }
        const pickMainObject = viewerPickMainObjectRef.current; // null after a partial deselect
        const matchingPaths = new Set<string>();
        let mainNodePath: string | null = null;
        for (const node of Object.values(treeRef.current)) {
            if (node.path === "") {
                continue;
            }
            if (highlightedIds.has(node.data.object.id)) {
                matchingPaths.add(node.path);
            }
            if (pickMainObject !== null && node.data.object.id === pickMainObject) {
                mainNodePath = node.path;
            }
        }
        setSelectedRowPaths(matchingPaths);
        if (mainNodePath && matchingPaths.has(mainNodePath)) {
            anchorRowPathRef.current = mainNodePath;
        }
    }, [highlightedIds]);

    const handleRowSelect = useCallback(
        async (path: string, event: React.MouseEvent) => {
            const node = tree[path];
            if (!node) {
                return;
            }
            const generation = ++selectGenerationRef.current;
            pendingViewerSelectRef.current = null;
            isViewerPickRef.current = false;
            viewerPickMainObjectRef.current = null;

            const mode = getSelectionMode(event, anchorRowPathRef.current !== null);
            const { paths: newPaths, nextAnchor } = computeNewSelectedPaths(
                mode,
                path,
                list,
                selectedRowPaths,
                anchorRowPathRef.current,
            );
            anchorRowPathRef.current = nextAnchor;
            setSelectedRowPaths(newPaths);

            // Toggle mode (cmd/ctrl+click): add or remove just the clicked node's hierarchy
            // additively — never clear all highlights — so selections built across multiple
            // different searches accumulate in the viewer instead of being wiped.
            if (mode === SelectionMode.Toggle) {
                const isAdding = newPaths.has(path);
                try {
                    if (isAdding) {
                        await selectObjectHierarchy({
                            nodes: [node.data.object],
                            db,
                            abortSignal: abortController.current.signal,
                            setLoading,
                            dispatchHighlighted,
                        });
                    } else {
                        await unselectObjectHierarchy({
                            nodes: [node.data.object],
                            db,
                            abortSignal: abortController.current.signal,
                            setLoading,
                            dispatchHighlighted,
                        });
                    }
                } catch (error) {
                    if (!abortController.current.signal.aborted) {
                        telemetry.trackCaughtException({
                            source: "objectTreeWindowV2.handleRowSelect",
                            category: "data",
                            error,
                            severity: "error",
                        });
                    }
                }
                if (selectGenerationRef.current !== generation) {
                    return;
                }
                if (isAdding) {
                    treeInitiatedRef.current = true;
                    dispatch(renderActions.setMainObject(node.data.object.id));
                } else if (newPaths.size === 0) {
                    treeInitiatedRef.current = true;
                    dispatch(renderActions.setMainObject(undefined));
                }
                return;
            }

            // Single / Range: clear all highlights then re-add every selected node.
            // Split into leaf and non-leaf: selectObjectHierarchy batches all leaf IDs
            // in one dispatch, but only processes nodes[0] for non-leaf.
            const leafObjects: ModelTreeNodeData["object"][] = [];
            const nonLeafObjects: ModelTreeNodeData["object"][] = [];
            for (const p of newPaths) {
                const n = tree[p];
                if (!n) {
                    continue;
                }
                if (n.isLeaf) {
                    leafObjects.push(n.data.object);
                } else {
                    nonLeafObjects.push(n.data.object);
                }
            }

            if (leafObjects.length > 0 || nonLeafObjects.length > 0) {
                dispatchHighlighted(highlightActions.setIds([]));
                try {
                    if (leafObjects.length > 0) {
                        await selectObjectHierarchy({
                            nodes: leafObjects,
                            db,
                            abortSignal: abortController.current.signal,
                            setLoading,
                            dispatchHighlighted,
                        });
                    }
                    if (nonLeafObjects.length > 0 && !abortController.current.signal.aborted) {
                        setLoading(true);
                        try {
                            await bulkSelectAllHierarchies({
                                nodes: nonLeafObjects,
                                db,
                                abortSignal: abortController.current.signal,
                                dispatchHighlighted,
                            });
                        } finally {
                            setLoading(false);
                        }
                    }
                } catch (error) {
                    if (!abortController.current.signal.aborted) {
                        telemetry.trackCaughtException({
                            source: "objectTreeWindowV2.handleRowSelect",
                            category: "data",
                            error,
                            severity: "error",
                        });
                    }
                }
                if (selectGenerationRef.current !== generation) {
                    return;
                }
                treeInitiatedRef.current = true;
                dispatch(renderActions.setMainObject(node.data.object.id));
            } else {
                dispatchHighlighted(highlightActions.setIds([]));
                dispatch(renderActions.setMainObject(undefined));
            }
        },
        [tree, list, selectedRowPaths, db, dispatch, dispatchHighlighted, abortController],
    );

    // Visibility button clicked on a selected row — act on all selected rows.
    // hide: clears selection; show: keeps selection.
    const handleToggleVisibilityForSelection = useCallback(
        async (hoveredIsHidden: boolean) => {
            const objects = [...selectedRowPaths]
                .map((p) => tree[p]?.data.object)
                .filter((o): o is ModelTreeNodeData["object"] => o != null);
            if (hoveredIsHidden) {
                for (const obj of objects) {
                    if (abortController.current.signal.aborted) {
                        break;
                    }
                    await showObjectHierarchy({
                        nodes: [obj],
                        db,
                        abortSignal: abortController.current.signal,
                        setLoading,
                        dispatchHidden,
                    });
                }
            } else {
                for (const obj of objects) {
                    if (abortController.current.signal.aborted) {
                        break;
                    }
                    await hideObjectHierarchy({
                        nodes: [obj],
                        db,
                        abortSignal: abortController.current.signal,
                        setLoading,
                        dispatchHidden,
                    });
                }
                setSelectedRowPaths(new Set());
                anchorRowPathRef.current = null;
            }
        },
        [selectedRowPaths, tree, db, abortController, dispatchHidden],
    );

    // Right-click: if the row is not in the selection, collapse to just that row first.
    const handleNodeRightClick = useCallback(
        async (_x: number, _y: number, item: ListNode<ModelTreeNodeData>) => {
            const path = item.node.path;
            if (!selectedRowPaths.has(path)) {
                const newPaths = new Set([path]);
                setSelectedRowPaths(newPaths);
                anchorRowPathRef.current = path;
                pendingViewerSelectRef.current = null;
                dispatchHighlighted(highlightActions.setIds([]));
                await selectObjectHierarchy({
                    nodes: [item.node.data.object],
                    db,
                    abortSignal: abortController.current.signal,
                    setLoading,
                    dispatchHighlighted,
                });
                treeInitiatedRef.current = true;
                dispatch(renderActions.setMainObject(item.node.data.object.id));
            }
            setContextMenu({ x: _x, y: _y });
        },
        [selectedRowPaths, db, dispatch, dispatchHighlighted, abortController],
    );

    const contextMenuActions = useMemo((): BulkAction[] => {
        const close = () => setContextMenu(null);
        return [
            {
                id: "hide-selection",
                label: allSelectedHidden ? t("unhideSelection") : t("hideSelection"),
                onClick: () => {
                    close();
                    void track(Events.bimObjects.hidden, { origin: EventOrigin.ObjectTree });
                    void handleToggleVisibilityForSelection(allSelectedHidden);
                },
                disabled: false,
            },
            {
                id: "zoom-to-selection",
                label: t("zoomToSelection"),
                onClick: () => {
                    void flyToSelected();
                    void track(Events.bimObjects.zoomedToSelection, { origin: EventOrigin.ObjectTree });
                    close();
                },
                disabled: false,
            },
            {
                id: "isolate",
                label: isIsolated ? t("unisolate") : t("isolate"),
                onClick: () => {
                    if (isIsolated) {
                        unisolateObjects({
                            dispatch,
                            restoreToVisibility: preIsolationVisibility ?? ObjectVisibility.Neutral,
                        });
                        void track(Events.bimObjects.unisolated, { origin: EventOrigin.ObjectTree });
                    } else {
                        isolateObjects({
                            dispatch,
                            currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
                        });
                        void track(Events.bimObjects.isolated, { origin: EventOrigin.ObjectTree });
                    }
                    close();
                },
                disabled: false,
            },
            {
                id: "isolate-xray",
                label: isIsolatedInXRay ? t("unisolateFromXRay") : t("isolateInXRay"),
                onClick: () => {
                    if (isIsolatedInXRay) {
                        unisolateObjectsFromXRayMode({
                            dispatch,
                            restoreToVisibility: preIsolationVisibility ?? ObjectVisibility.Neutral,
                        });
                        void track(Events.bimObjects.unisolatedFromXray, { origin: EventOrigin.ObjectTree });
                    } else {
                        isolateObjectsInXRayMode({
                            dispatch,
                            currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
                        });
                        void track(Events.bimObjects.isolatedInXray, { origin: EventOrigin.ObjectTree });
                    }
                    close();
                },
                disabled: false,
            },
            {
                id: "deselect-selection",
                label: t("deselectSelection"),
                dividerAbove: true,
                onClick: () => {
                    setSelectedRowPaths(new Set());
                    anchorRowPathRef.current = null;
                    dispatchHighlighted(highlightActions.setIds([]));
                    dispatch(renderActions.setMainObject(undefined));
                    close();
                },
                disabled: false,
            },
        ];
    }, [
        allSelectedHidden,
        isIsolated,
        isIsolatedInXRay,
        t,
        handleToggleVisibilityForSelection,
        flyToSelected,
        dispatch,
        preIsolationVisibility,
        defaultVisibility,
        dispatchHighlighted,
    ]);

    const selectionContext = useMemo(
        () => ({
            selectedRowPaths,
            list,
            onRowSelect: handleRowSelect,
            onToggleVisibilityForSelection: handleToggleVisibilityForSelection,
        }),
        [selectedRowPaths, list, handleRowSelect, handleToggleVisibilityForSelection],
    );

    return (
        <SlottedWindow
            position={position}
            size={{ width: 400, height: 600 }}
            onClose={onClose}
            loading={isSearching || loading}
            headerRegion={<SlottedWindow.Title>Object Tree V2</SlottedWindow.Title>}
            actions={[
                {
                    icon: <ExternalLink style={{ width: 16, height: 16 }} />,
                    label: "Open in new window",
                    onClick: () => {},
                },
            ]}
            subheaderRegion={
                <SlottedWindow.SubheaderRow style={{ padding: "0px 16px 12px 16px" }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            height: 30,
                            borderRadius: 4,
                            backgroundColor: "#EEF0F1",
                            paddingLeft: 12,
                            paddingRight: 8,
                            gap: 4,
                        }}
                    >
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder={`${t("search")}...`}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                background: "transparent",
                                outline: "none",
                                border: "none",
                                fontFamily: "inherit",
                                fontSize: 12,
                                fontWeight: 400,
                                letterSpacing: "0.25px",
                                lineHeight: "16px",
                                color: "#111827",
                            }}
                            autoComplete="off"
                        />
                        <Search style={{ flexShrink: 0, width: 16, height: 16, color: "#232729" }} />
                    </div>
                </SlottedWindow.SubheaderRow>
            }
        >
            {/* Clicking empty space below rows deselects all (req 21) */}
            <div
                style={{
                    height: "100%",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
                onClick={() => {
                    setSelectedRowPaths(new Set());
                    anchorRowPathRef.current = null;
                    dispatchHighlighted(highlightActions.setIds([]));
                    dispatch(renderActions.setMainObject(undefined));
                }}
            >
                <SelectionContext.Provider value={selectionContext}>
                    <ModelTree
                        key={treeKey}
                        tree={tree}
                        setTree={setTree}
                        fileTypes={emptyFileTypeFilters}
                        NodeRenderer={ObjectTreeNodeProcoreV2}
                        itemSize={ITEM_HEIGHT_V2}
                        filteredObjectIds={filteredObjectIds}
                        isFilterActive={isFilterActive}
                        searchTerm={searchTerm}
                        headerMode="sticky"
                        scrollAlignment="center"
                        noHorizontalScroll
                        onNodeRightClick={handleNodeRightClick}
                    />
                </SelectionContext.Provider>
            </div>

            <ContextMenuPopup
                contextMenu={
                    contextMenu ? { mouseX: contextMenu.x, mouseY: contextMenu.y, actions: contextMenuActions } : null
                }
                onClose={() => setContextMenu(null)}
            />
        </SlottedWindow>
    );
}
